import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  ApplicationState,
  getEbuOrderingError,
  getEbuOrderingInput,
  getEbuOrderingStatus,
  isEbuOrdering,
} from 'src/app/store/reducers';
import { Store } from '@ngrx/store';
import {
  OrderEBUOption,
  OrderEBUOptionConfirm,
  OrderEBUOptionValidation,
} from 'src/app/store/actions/ebu-ordering.actions';
import { OnDestroyHookDirective } from '../hooks';
import {
  Alert,
  AlertsService,
  ModalService,
} from '@proximus/ng-common-components';
import { OrderingStatusDataModel } from '../models/ebu-ordering.model';
import { EbuOrderConfirmationModalComponent } from 'src/app/details/option-details/ebu-order-confirmation-modal/ebu-order-confirmation-modal.component';
import {
  isNetflixBuyAndSell,
  NetflixBuyAndSellStatus,
} from '@dch/products-business-logic';
import {
  getProductUuid,
  isOSDOption,
} from '../ebu-order-utils/ebu-order-utils';
import {
  CookieService,
  EnvironmentService,
  TranslationService,
} from '@proximus/ng-common';
import { EbuOrderValidationModalComponent } from 'src/app/details/option-details/ebu-order-validation-modal/ebu-order-validation-modal.component';
import { LoggerService } from '../../services/logger.service';
import Cookies from 'js-cookie';

@Component({
  selector: 'mprd-ebu-order-button',
  templateUrl: './ebu-order-button.component.html',
})
export class EbuOrderButtonComponent
  extends OnDestroyHookDirective
  implements OnChanges, OnInit
{
  public orderLabelKey: string;
  @Input() option;
  @Input() selectedProduct;
  @Input() isPayDateInFuture;
  @Output() resultEmit = new EventEmitter();
  orderingInput;
  error;
  active;
  isOSDOption;
  public loading: boolean;
  public ceaseLabelKey: string;
  public disableButtonClass = '';
  public negativeButtonClass: string;

  constructor(
    private store: Store<ApplicationState>,
    private modalService: ModalService,
    private cookieService: CookieService,
    private environmentService: EnvironmentService,
    private alertService: AlertsService,
    private translationService: TranslationService,
    private loggerService: LoggerService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.addSubscription(
      this.store.select(getEbuOrderingInput).subscribe((value) => {
        this.orderingInput = value;
      }),
    );

    this.addSubscription(
      this.store.select(getEbuOrderingError).subscribe((value) => {
        this.error = value;
      }),
    );

    this.addSubscription(
      this.store.select(isEbuOrdering).subscribe((value) => {
        this.loading = value;
      }),
    );

    this.addSubscription(
      this.store.select(getEbuOrderingStatus).subscribe((value) => {
        if (value === OrderingStatusDataModel.CONFIRMATION_REQUIRED) {
          this.showConfirmationEbuOrderingPopup();
        } else if (value === OrderingStatusDataModel.VALIDATION_REQUIRED) {
          this.showValidationEbuOrderingPopup();
        } else if (value === OrderingStatusDataModel.ERROR) {
          this.dispatchAlert(
            'error',
            this.translationService.getTranslatedValueFor(
              'message.error.title',
            ),
            this.error,
          );
        } else if (value === OrderingStatusDataModel.SUCCESS) {
          this.dispatchAlert(
            'success',
            this.translationService.getTranslatedValueFor(
              'message.confirmation.title',
            ),
            this.error,
          );
        }
      }),
    );

    // workaround to set the iiamsid cookie from session storage
    this.setIiamsidCookie();
    this.listAllCookies();
  }

  ngOnChanges(simpleChanges: SimpleChanges): void {
    if (
      this.option &&
      this.selectedProduct &&
      simpleChanges.hasOwnProperty('option') &&
      JSON.stringify(simpleChanges.option.previousValue) !==
        JSON.stringify(simpleChanges.option.currentValue) &&
      simpleChanges.hasOwnProperty('selectedProduct') &&
      JSON.stringify(simpleChanges.selectedProduct.previousValue) !==
        JSON.stringify(simpleChanges.selectedProduct.currentValue)
    ) {
      this.internalOnChanges();
    }
  }

  internalOnChanges() {
    if (this.option && this.selectedProduct) {
      this.active = this.option.ibStatus === 'ACT';
      this.isOSDOption = isOSDOption(this.option);
      this.ceaseLabelKey =
        isNetflixBuyAndSell(this.option) &&
        NetflixBuyAndSellStatus.ACTIVE ===
          this.selectedProduct.netflixBuyAndSell
          ? 'option.details.downgrade'
          : 'option.details.cease';
    }
  }

  addToCart() {
    this.store.dispatch(
      new OrderEBUOption({ optionId: this.option.optionCodeForDescription }),
    );
  }

  showConfirmationEbuOrderingPopup() {
    this.modalService
      .create<EbuOrderConfirmationModalComponent>(
        EbuOrderConfirmationModalComponent,
      )
      .subscribe((componentRef) => {
        componentRef.instance.option = this.option;
        componentRef.instance.confirm = () => this.confirmOrder();
        componentRef.instance.cancel = () => this.cancelOrder();
      });
  }

  showValidationEbuOrderingPopup() {
    this.modalService
      .create<EbuOrderValidationModalComponent>(
        EbuOrderValidationModalComponent,
      )
      .subscribe((componentRef) => {
        componentRef.instance.option = this.option;
        componentRef.instance.confirm = () => this.validateOrder();
        componentRef.instance.cancel = () => this.cancelValidationOrder();
        componentRef.instance.orderingInput = this.orderingInput;
      });
  }

  // confirmation
  confirmOrder() {
    this.store.dispatch(
      new OrderEBUOptionConfirm({
        productId: getProductUuid(this.selectedProduct),
        optionId: this.option.optionCodeForDescription,
        cancel: false,
      }),
    );
  }

  cancelOrder() {
    this.store.dispatch(
      new OrderEBUOptionConfirm({
        productId: getProductUuid(this.selectedProduct),
        optionId: this.option.optionCodeForDescription,
        cancel: true,
      }),
    );
  }

  // validation
  validateOrder() {
    this.store.dispatch(
      new OrderEBUOptionValidation({
        orderingInput: this.orderingInput,
        cancel: false,
        productId: getProductUuid(this.selectedProduct),
        optionId: this.option.optionCodeForDescription,
      }),
    );
  }

  cancelValidationOrder() {
    this.store.dispatch(
      new OrderEBUOptionValidation({
        orderingInput: this.orderingInput,
        cancel: true,
        productId: getProductUuid(this.selectedProduct),
        optionId: this.option.optionCodeForDescription,
      }),
    );
  }

  private dispatchAlert(
    type: 'error' | 'info' | 'success' | 'warning',
    title: string,
    description: string,
  ): void {
    const alert: Alert = {
      isClosable: true,
      alertKey: 'ebuOrderingAlert',
      title,
      description,
      type,
      scrollTo: true,
      scrollOffset: 200,
    };
    this.alertService.dispatchAlert(alert);
  }

  private getMyProximusAuthenticatedCookieName() {
    if (this.environmentService.isProd || this.environmentService.isDR) {
      return 'iiamsid';
    } else if (this.environmentService.isUat) {
      return 'iiamsid-uat';
    } else {
      return 'iiamsid-itt';
    }
  }

  private setIiamsidCookie() {
    const iIamCookie = this.getMyProximusAuthenticatedCookieName();
    this.loggerService.log('set "' + iIamCookie + '" cookie');
    const iiamsidFromStorage = sessionStorage.getItem(iIamCookie);
    this.loggerService.log(
      iIamCookie + ': ' + sessionStorage.getItem(iIamCookie),
    );
    if (iiamsidFromStorage) {
      this.cookieService.setCookie(
        iIamCookie,
        sessionStorage.getItem(iIamCookie),
        1,
      );
    } else {
      this.loggerService.log(
        'no "' + iIamCookie + '" cookie found in session storage',
      );
    }
  }

  private listAllCookies() {
    this.loggerService.log('visible cookie list (http only cookies excluded)');
    const theCookies = document.cookie.split(';');
    this.loggerService.log('---START---');
    theCookies.forEach((cookie) => this.loggerService.log(cookie + ';'));
    this.loggerService.log('---END---');
  }
}
